import { CommonModule } from '@angular/common';
import {
  // ChangeDetectionStrategy,
  Component,
  ContentChildren,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormArray, FormControl, FormGroup, FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import {
  BaseCheckboxComponent,
  BaseDropdownComponent,
  BaseInputComponent,
  BaseLabelComponent,
  DeletePopupComponent,
} from '@d-components';
import { BaseButtonComponent } from '@d-components/button/button.component';
import { BaseFormComponent } from '@d-components/form/form.component';
import { ImportPopupComponent } from '@d-components/popup/import-popup/import-popup.component';
import {
  DEFAULT_FORMAT_DATE,
  DEFAULT_FORMAT_DATETIME,
  FILTER_ORDER,
  ModalSize,
  SystemStatus,
} from '@d-constants';
import { ACTION_PERMISSION } from '@d-constants/permission.constant';
import { ModalService, RoleService } from '@d-services';
import { AuditService } from '@d-services/wms/audit.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PermissionDirective } from 'app/core/directives/permission.directive';
import dayjs from 'dayjs';
import { isArray, isEqual, get, difference, concat, isObject } from 'lodash-es';
import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { CheckboxModule } from 'primeng/checkbox';
import { InputSwitchModule } from 'primeng/inputswitch';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { EditableRow, Table, TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { Subject } from 'rxjs';
import { CustomCellDirective } from './custom-cell.directive';
import {
  ColumnConfig,
  COLUMN_TYPE,
  CustomCellConfig,
  isTextWithSubColumnConfig,
  TableConfig,
} from './table.interface';
import { Paginator } from 'primeng/paginator';

@Component({
  selector: 'app-base-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    OverlayPanelModule,
    TableModule,
    ButtonModule,
    DropdownModule,
    CheckboxModule,
    BaseButtonComponent,
    BaseFormComponent,
    TranslateModule,
    TooltipModule,
    BaseCheckboxComponent,
    BaseLabelComponent,
    CustomCellDirective,
    BaseDropdownComponent,
    BaseInputComponent,
    BadgeModule,
    InputSwitchModule,
    FormsModule,
    PermissionDirective,
  ],
  providers: [EditableRow],
  // changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BaseTableComponent implements OnInit, OnChanges {
  COLUMN_TYPE = COLUMN_TYPE;
  @Input() tableConfig: TableConfig<any> = {
    selectable: false,
    dataKey: 'id',
    columnsConfig: [],
    toolbarConfig: {
      display: false,

      multipleDelete: {
        onDelete: (items) => {},
        confirmPopup: DeletePopupComponent,
        confirmPopupParams: {},
      },
      multipleDownload: {
        onDownload: (items) => {},
      },
    },
  };
  ACTION_PERMISSION = ACTION_PERMISSION;
  @Input() moduleCode: string | string[];
  @Input() editInline: boolean = false;
  @Input() border: boolean = false;
  @Input() dataTable: any[] = [];
  @Input() paginator: boolean = true;
  @Input() totalRecords!: number;
  @Input() rows: number = 15;
  @Input() emptyDataMessage: string = '';
  @Input() first = 0;
  @Input() defaultSort: any = [
    { field: 'updatedDateTime', order: FILTER_ORDER.DESC },
  ];
  @Input() pageOne: boolean = false;
  @Input() clientPaginator: boolean = false;
  @Input() selectedRows: any = [];
  @Input() keepSelectedRow = false;
  @Input() tableOptions = { scrollable: true, scrollHeight: '600px' };
  @Output() onLazyLoad = new EventEmitter();
  @Output() onSelectionChange = new EventEmitter();
  @Output() onHeaderCheckboxToggle = new EventEmitter();
  @Output() onSaveNew = new EventEmitter();
  @Output() onSearch = new EventEmitter();
  @Output() onExport = new EventEmitter();
  @Output() onInBulk = new EventEmitter();
  @Output() pageOneChange = new EventEmitter<boolean>();
  @Output() onInitTableForm = new EventEmitter();
  @ViewChild(Table, { static: true }) table!: Table;
  @ContentChildren(CustomCellDirective, {
    descendants: true,
    read: CustomCellDirective,
  })
  allCustomCell!: QueryList<CustomCellDirective>;
  onLazyLoadTrigger: Subject<any> = new Subject();

  currentCellControl = new FormArray([]);

  destroy$: Subject<boolean> = new Subject<boolean>();

  // ONLY FOR PAGINATOR ON CLIENT SIDE
  originalDataTable = structuredClone(this.dataTable);

  currentEvent: any = {
    first: this.first,
    rowPerPage: this.rows,
    pageIndex: 1,
    sortBy: this.defaultSort,
  };

  actionType = [COLUMN_TYPE.ListActionIcon, COLUMN_TYPE.MoreAction];

  // addNew variables zone
  addNew = false;
  addNewForm!: FormGroup;

  searchable = false;
  searchForm!: FormGroup;

  constructor(
    private modalService: ModalService,
    private roleService: RoleService,
    private route: ActivatedRoute,
    private auditService: AuditService,
    private translateService: TranslateService,
  ) {
    this.isRowSelectable = this.isRowSelectable.bind(this);
  }

  exportTableType = [
    {
      label: 'PDF',
      command: () => {
        this.auditService.createEventLog(
          this.translateService.instant(this.route.snapshot.data.title),
          ACTION_PERMISSION.EXPORT,
        );
        this.onExport.emit({ type: 'PDF' });
      },
    },
    {
      label: 'EXCEL',
      command: () => {
        this.auditService.createEventLog(
          this.translateService.instant(this.route.snapshot.data.title),
          ACTION_PERMISSION.EXPORT,
        );
        this.onExport.emit({ type: 'EXCEL' });
      },
    },
    {
      label: 'CSV',
      command: () => {
        this.auditService.createEventLog(
          this.translateService.instant(this.route.snapshot.data.title),
          ACTION_PERMISSION.EXPORT,
        );
        this.onExport.emit({ type: 'CSV' });
      },
    },
  ];

  handleOnInBulk(event: any) {
    this.onInBulk.emit(event);
  }

  // inBulkTableType = [
  //   {s
  //     label: 'Import Work Order',
  //     command: () => {
  //       this.auditService.createEventLog(
  //         this.translateService.instant(this.route.snapshot.data.title),
  //         ACTION_PERMISSION.IN_BULK,
  //       );
  //       this.onInBulk.emit({ type: 'ImportWorkOrder' });
  //     },
  //   },
  //   {
  //     label: 'Work Order Bulk Status',
  //     command: () => {
  //       this.auditService.createEventLog(
  //         this.translateService.instant(this.route.snapshot.data.title),
  //         ACTION_PERMISSION.IN_BULK,
  //       );
  //       this.onInBulk.emit({ type: 'WorkOrderBulkStatus' });
  //     },
  //   }
  // ];

  ngOnInit(): void {
    this.overridePrimeNGTableMethods();
    this.tableConfig.dataKey = this.tableConfig.dataKey || 'id';
    this.tableConfig.columnsConfig = this.tableConfig.columnsConfig.filter(
      (i) => !i.hidden,
    );
    if (this.editInline) {
      setTimeout(() => {
        this.onInitTableForm.emit(this.currentCellControl);
      }, 100);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.hasOwnProperty('pageOne') && this.pageOne) {
      this.table.onPageChange({
        first: 0,
        rows:
          this.currentEvent.first === 0
            ? this.currentEvent.rowPerPage
            : this.rows,
      });
    }
    if (changes.hasOwnProperty('dataTable')) {
      if (this.clientPaginator) {
        this.originalDataTable = structuredClone(this.dataTable);
        this.totalRecords = this.originalDataTable.length;
        this.handleLoadClient();
      }

      if (!this.keepSelectedRow && this.selectedRows.length)
        this.selectedRows = [];
    }

    if (changes.hasOwnProperty('tableConfig')) {
      this.tableConfig.columnsConfig = this.tableConfig.columnsConfig.filter(
        (i) => !i.hidden,
      );
      // if (this.editInline) this.updateCellControl();
    }
    if (
      (changes.hasOwnProperty('first') && this.first > 0) ||
      (changes.hasOwnProperty('rows') && this.rows)
    ) {
      this.currentEvent = {
        first: this.first || 0,
        rowPerPage: this.rows || 15,
        pageIndex: Math.floor(this.first / this.rows) + 1,
        sortBy: this.defaultSort,
      };
    }
  }

  isRowSelectable(event) {
    return !this.isDisabledRowCheckbox(event.data);
  }

  isDisabledRowCheckbox(data) {
    return this.tableConfig.isRowSelectable
      ? !this.tableConfig.isRowSelectable(data)
      : false;
  }

  getDataByPath(data: any, path: string = '', rowIndex = -1) {
    if (this.editInline && rowIndex >= 0) {
      return this.getFieldControlDisplay(rowIndex, path)?.value || '';
    }
    if (path && path.includes(',')) return 'Invalid path';
    const pathArr = path?.replace(/[\[\]\.]+/gi, ',').split(',');
    let rs = data;
    pathArr.forEach((i: string) => {
      rs = rs[i] ?? rs;
    });
    return typeof rs === 'object' && !Array.isArray(rs) ? '' : rs;
  }

  checkPaginator() {
    return this.paginator && this.dataTable?.length > 0;
  }

  handleOnClickItem(item: any, data: any) {
    item.action.execute(data);
  }

  /**
   * Trigger when inittable, page change, page-size change, sort colum change
   * @param $event
   *
   */
  handleLazyLoad($event: any) {
    if (
      $event.multiSortMeta &&
      $event.multiSortMeta.some((i: any) => !i.field)
    ) {
      return;
    }
    const newEvent = {} as any;
    newEvent.sortBy = !$event.multiSortMeta
      ? []
      : this.flattenMultiSortMeta($event.multiSortMeta).map((i: any) => ({
          fieldName: i.field,
          ascending: i.order > 0,
        }));
    if (this.currentEvent.rowPerPage !== $event.rows) {
      newEvent.first = 0;
      newEvent.pageIndex = 1;
    } else {
      newEvent.first = $event.first;
      newEvent.pageIndex = Math.floor($event.first / $event.rows) + 1;
    }
    this.first = newEvent.first;
    newEvent.rowPerPage = $event.rows ?? $event.rowPerPage;
    this.pageOneChange.emit(newEvent.first === 0);
    newEvent.sortField = $event.sortField;
    newEvent.sortOrder = $event.sortOrder > 0 ? 'asc' : 'desc';
    newEvent.typeChange = this.getTableTypeChange(newEvent);
    this.currentEvent = newEvent;
    if (this.clientPaginator) {
      this.handleLoadClient();
    } else {
      this.onLazyLoad.emit(newEvent);
    }
  }

  flattenMultiSortMeta(multiSortMeta: any): any[] {
    return multiSortMeta.reduce((result: any[], current: any) => {
      if (isArray(current.field)) {
        result = [
          ...result,
          ...current.field.map((field: any) => ({
            ...current,
            field,
          })),
        ];
      } else {
        result = [...result, current];
      }
      return result;
    }, []);
  }

  handleImport() {
    if (this.tableConfig.importable) {
      this.modalService
        .openModal(ImportPopupComponent, {
          title: this.tableConfig.importable.title,
          size: ModalSize.LARGE,
          showIconClose: false,
          importFunc: this.tableConfig.importable.importFunc,
          submitFunc: this.tableConfig.importable.submitFunc,
          getTemplateFunc: this.tableConfig.importable.getTemplateFunc,
          templateName: this.tableConfig.importable.templateName,
          dataArea: this.tableConfig.importable.dataArea,
          submitSuccessMessage:
            this.tableConfig.importable.submitSuccessMessage,
          downloadMessage: this.tableConfig.importable.downloadMessage,
          overrideFlag: this.tableConfig.importable.overrideFlag,
        })
        .subscribe((val) => {
          if (val.actionType === 'submit') {
            this.handleLazyLoad(this.currentEvent);
          }
        });
    }
  }

  onRowSelect($event: any) {
    this.onSelectionChange.emit(this.selectedRows);
  }

  onRowUnselect(event: any) {
    this.onSelectionChange.emit(this.selectedRows);
  }

  onRowSelectAll(event: any) {
    this.onHeaderCheckboxToggle.emit(event);
    this.onSelectionChange.emit(this.selectedRows);
  }

  onRowUnselectAll() {
    this.selectedRows = [];
  }

  getMenuItem(item: any) {
    return item.menuItems;
  }

  deleteMultiple() {
    const popupParams =
      this.tableConfig.toolbarConfig?.multipleDelete?.confirmPopupParams;
    this.modalService
      .openModal(
        this.tableConfig.toolbarConfig?.multipleDelete?.confirmPopup ||
          DeletePopupComponent,
        { title: 'DELETE', hideLengthCount: true, ...popupParams },
      )
      .subscribe((val) => {
        if (val.actionType === 'submit')
          this.tableConfig.toolbarConfig?.multipleDelete?.onDelete(
            this.selectedRows,
            val.reason,
          );
      });
  }

  multipleDownload() {
    this.tableConfig.toolbarConfig?.multipleDownload?.onDownload(
      this.selectedRows,
    );
  }

  getSearchConfig(item: any) {
    return [
      {
        layoutDefine: { row: 0, column: 0 },
        fieldControl: this.searchForm.get(item.dataField),
        ...item.editConfig,
      },
    ];
  }

  onCancel() {
    if (this.addNew) this.addNew = false;
  }

  getCustomClassWrapper(item: any, rowData: any) {
    const customClass =
      ('' + this.getDataByPath(rowData, item.cssClassWrapperByData))
        .toLowerCase()
        .replace(/\d/gi, '')
        .replace(/ /gi, '_') || '';
    return item.cssClassWrapper + ' ' + customClass;
  }

  dateFormat(value: string, item: any = {}) {
    if (!value) return null;
    if (typeof value === 'number' || item.format == DEFAULT_FORMAT_DATE)
      return dayjs(value).format(item.format || DEFAULT_FORMAT_DATETIME);
    return dayjs(`${value}Z`).format(item.format || DEFAULT_FORMAT_DATETIME);
  }

  getDataCodeLabel(value: string | number, item: any) {
    return item.options && item.options.length
      ? item.options.find((i: any) => i.value === value)?.label
      : '';
  }

  getDataCodeLabelForSub(value: string | number, item: any) {
    return item.subStatusOption && item.subStatusOption.length
      ? item.subStatusOption.find((i: any) => i.value === value)?.label
      : '';
  }

  getStatusLabel(value: string | number, item?: any) {
    return (
      (item?.statusOptions
        ? item.statusOptions.find((i: any) => i.value === value)?.label
        : SystemStatus.find((i: any) => i.value === value)?.label) || ''
    );
  }

  getStatusLabelSubField(value: string | number, item?: any) {
    return (
      (item?.subStatusOption
        ? item.subStatusOption.find((i: any) => i.value === value)?.label
        : SystemStatus.find((i: any) => i.value === value)?.label) || ''
    );
  }

  getStatusColorStyle(
    data: any,
    field: string,
    items?: any,
    isSubColumn = false,
  ) {
    let statusLabel = this.getStatusLabel(
      this.getDataByPath(data, field || ''),
      items,
    );
    let statusOptions = isSubColumn
      ? items?.subStatusOption
      : items?.statusOptions
      ? items.statusOptions
      : SystemStatus;
    if (
      statusLabel ===
      statusOptions.find((x: any) => x.label === 'APPROVED')?.label
    ) {
      return 'status-border-teal status-bg-teal status-text-teal ';
    }

    if (
      statusLabel ===
        statusOptions.find((x: any) => x.label === 'NEW')?.label ||
      statusLabel ===
        statusOptions.find((x: any) => x.label === 'DRAFT')?.label ||
      statusLabel ===
        statusOptions.find((x: any) => x.label === 'INACTIVE')?.label
    ) {
      return 'status-border-gray status-bg-gray status-text-gray ';
    }

    if (
      statusLabel ===
        statusOptions.find((x: any) => x.label === 'ACTIVE')?.label ||
      statusLabel ===
        statusOptions.find((x: any) => x.label === 'AUTHORIZED')?.label ||
      statusLabel ===
        statusOptions.find((x: any) => x.label === 'POSTED')?.label ||
      statusLabel ===
        statusOptions.find((x: any) => x.label === 'COMPLETED')?.label ||
      statusLabel ===
        statusOptions.find((x: any) => x.label === 'CLOSED')?.label
    ) {
      return 'status-border-green status-bg-green status-text-green ';
    }

    if (
      statusLabel ===
        statusOptions.find((x: any) => x.label === 'REJECTED')?.label ||
      statusLabel ===
        statusOptions.find((x: any) => x.label === 'FAILED')?.label ||
      statusLabel ===
        statusOptions.find((x: any) => x.label === 'ERROR')?.label ||
      statusLabel ===
        statusOptions.find((x: any) => x.label === 'CANCELLED')?.label
    ) {
      return 'status-border-red status-bg-red status-text-red ';
    }

    if (
      statusLabel ===
        statusOptions.find((x: any) => x.label === 'NEW')?.label ||
      statusLabel ===
        statusOptions.find((x: any) => x.label === 'PROCESSING')?.label ||
      statusLabel ===
        statusOptions.find((x: any) => x.label === 'PENDING')?.label ||
      statusLabel ===
        statusOptions.find((x: any) => x.label === 'PENDING_ERP_RESPONSE')
          ?.label ||
      statusLabel ===
        statusOptions.find((x: any) => x.label === 'PENDING_KYC')?.label ||
      statusLabel ===
        statusOptions.find(
          (x: any) => x.label === 'PENDING_1ST_LEVEL_KYC_APPROVAL',
        )?.label ||
      statusLabel ===
        statusOptions.find(
          (x: any) => x.label === 'PENDING_2ND_LEVEL_KYC_APPROVAL',
        )?.label ||
      statusLabel ===
        statusOptions.find((x: any) => x.label === 'PENDING_FINAL_APPROVAL')
          ?.label ||
      statusLabel ===
        statusOptions.find(
          (x: any) => x.label === 'PENDING_MERCHANT_AUTHORIZATION',
        )?.label ||
      statusLabel ===
        statusOptions.find((x: any) => x.label === 'PLANNED')?.label
    ) {
      return 'status-border-yellow status-bg-yellow status-text-yellow ';
    }
    if (statusLabel === '') {
      return 'border-0';
    }

    return 'status-border-blue status-bg-blue status-text-blue ';
  }

  getStatusClassWrapper(item: any, data: any) {
    return (
      (item.cssClassWrapper || '') +
        ' ' +
        this.getStatusLabel(this.getDataByPath(data, item.dataField), item)
          .toLowerCase()
          .replace(/\d/gi, '')
          .replace(/ /gi, '_') || ''
    );
  }

  checkPermission(item, data) {
    if (!item.actionPermission) return true;
    const moduleCode =
      item.actionPermission.moduleCode ||
      this.moduleCode ||
      this.route.snapshot.data['moduleCode'];
    return this.roleService.checkPermissionByAction(
      moduleCode,
      item.actionPermission.actionName,
      data.createdBy,
    );
  }

  handleClickCell(event: MouseEvent, config: ColumnConfig<any>, dataItem: any) {
    if (
      config.actionPermission &&
      !this.roleService.checkPermissionByAction(
        this.moduleCode || this.route.snapshot.data['moduleCode'],
        config.actionPermission.actionName,
        dataItem.createdBy,
      )
    )
      return;

    if (config.onClick) {
      config.onClick(event, dataItem);
    } else if (isTextWithSubColumnConfig(config) && config.textWithSubOnclick) {
      config.textWithSubOnclick(event, dataItem);
    }
  }

  checkTextWithSubHaveOnClick(config: ColumnConfig<any>) {
    return isTextWithSubColumnConfig(config) && config.textWithSubOnclick;
  }

  getCustomCell(item: CustomCellConfig) {
    const { cellName } = item;
    if (!this.allCustomCell.length) return;
    const ref = this.allCustomCell.find(
      (cell) => cell.customCell === cellName,
    ) as any;

    return ref.el;
  }

  handleLoadClient() {
    const sortedData = this.currentEvent.sortBy.length
      ? structuredClone(this.originalDataTable)?.sort((a: any, b: any) => {
          if (
            this.currentEvent.sortBy[0].fieldName
              ?.toLowerCase()
              ?.includes?.('time') ||
            this.currentEvent.sortBy[0].fieldName
              ?.toLowerCase()
              ?.includes?.('date')
          ) {
            if (
              dayjs(a[this.currentEvent.sortBy[0].fieldName]).isBefore(
                dayjs(b[this.currentEvent.sortBy[0].fieldName]),
              )
            ) {
              return this.currentEvent.sortBy[0].ascending ? 1 : -1;
            }
            return this.currentEvent.sortBy[0].ascending ? -1 : 1;
          }
          const dataType = typeof this.getDataByPath(
            this.dataTable[0],
            this.currentEvent.sortBy[0].fieldName,
          );
          if (dataType === 'number') {
            if (
              this.getDataByPath(a, this.currentEvent.sortBy[0].fieldName) >
              this.getDataByPath(b, this.currentEvent.sortBy[0].fieldName)
            )
              return this.currentEvent.sortBy[0].ascending ? 1 : -1;
          } else {
            if (
              this.getDataByPath(
                a,
                this.currentEvent.sortBy[0].fieldName,
              )?.toLowerCase() >
              this.getDataByPath(
                b,
                this.currentEvent.sortBy[0].fieldName,
              )?.toLowerCase()
            )
              return this.currentEvent.sortBy[0].ascending ? 1 : -1;
          }

          if (
            this.getDataByPath(a, this.currentEvent.sortBy[0].fieldName) ===
            this.getDataByPath(b, this.currentEvent.sortBy[0].fieldName)
          )
            return 0;

          return this.currentEvent.sortBy[0].ascending ? -1 : 1;
        })
      : this.originalDataTable;
    this.dataTable = sortedData.slice(
      this.currentEvent.first,
      this.currentEvent.first + this.currentEvent.rowPerPage,
    );
    if (this.originalDataTable.length > 0 && this.dataTable.length === 0) {
      this.currentEvent = {
        ...this.currentEvent,
        first:
          this.currentEvent?.first - this.currentEvent.rowPerPage >= 0
            ? this.currentEvent.first - this.currentEvent.rowPerPage
            : 0,
      };
      this.handleLoadClient();
    }
  }

  private getTableTypeChange(newEvent: any) {
    if (this.currentEvent.rowPerPage !== newEvent.rowPerPage) {
      return 'PageSizeChange';
    }
    if (this.currentEvent.pageIndex !== newEvent.pageIndex) return 'PageChange';
    if (!isEqual(this.currentEvent.sortBy, newEvent.sortBy))
      return 'SortChange';
    return 'Other';
  }

  addRowsControls(rows) {
    // add row control
    rows.forEach((data) => {
      const newForm = new FormGroup({});
      const configColumns: any = this.tableConfig.columnsConfig.filter(
        (item) => item.dataField,
      );
      const hiddenFields = difference(
        Object.keys(data),
        configColumns.map((item) => item.dataField),
      ).map((item) => ({ dataField: item }));

      concat(hiddenFields, configColumns).forEach((config: any) => {
        const value = get(data, config.dataField, config.defaultValue || '');
        newForm.addControl(
          config.dataField,
          new FormControl(value, {
            validators: config.validators ? config.validators : [],
          }),
        );
      });
      this.currentCellControl.push(newForm, { emitEvent: false });
      this.dataTable.push(newForm.value);
    });
  }

  removeRowsControls(rows) {
    // remove rows controls
    if (rows && rows.length) {
      rows.forEach((i) => {
        this.currentCellControl.removeAt(i);
        this.dataTable = this.currentCellControl.value;
      });
    }
  }

  clearRowsControls() {
    this.dataTable = [];
    setTimeout(() => {
      this.currentCellControl.clear();
    }, 100);
  }

  // handle cell control for show/hide column
  // private updateCellControl() {
  //   if (this.currentCellControl.controls.length) {
  //     // remove cell control
  //     const configFields = this.tableConfig.columnsConfig
  //       .filter((item) => !!item.dataField)
  //       .map((config) => config.dataField);
  //     const initFields = Object.keys(
  //       this.currentCellControl.controls[0].value,
  //     ).filter((item) => item !== this.tableConfig.dataKey);

  //     const deletedFields = difference(initFields, configFields);
  //     const newFields = difference(configFields, initFields);
  //     this.currentCellControl.controls.forEach((control: FormGroup) => {
  //       if (deletedFields.length) {
  //         deletedFields.forEach((item) => {
  //           control.removeControl(item);
  //         });
  //       }
  //       if (newFields.length) {
  //         this.tableConfig.columnsConfig
  //           .filter((item) => newFields.includes(item.dataField))
  //           .forEach((config: any) => {
  //             control.addControl(
  //               config.dataField,
  //               new FormControl(config.defaultValue || '', {
  //                 validators: config.validators ? config.validators : [],
  //               }),
  //             );
  //           });
  //       }
  //     });
  //   }
  // }

  getFieldControlDisplay(rowIndex: number, dataField: string) {
    return this.currentCellControl.controls[rowIndex]?.get(dataField);
  }
  checkActionType(type?: COLUMN_TYPE) {
    return type ? this.actionType.includes(type) : true;
  }

  getDisabledControlCell<T>(item: any, data: T): boolean {
    if (!this.checkPermission(item, data)) return true;
    if (!item.disabled) return false;
    if (typeof item.disabled === 'function') return item.disabled(data);
    return item.disabled;
  }

  getDataDisplayWithCount(
    data: any,
    dataField: string,
    showField: string,
    separatorCharacter: string = ',',
  ) {
    const dataItem = this.getDataByPath(data, dataField);
    return Array.isArray(dataItem)
      ? dataItem
          .map((i: any) => (isObject(i) ? i[showField] : i))
          .join(separatorCharacter)
      : '';
  }

  private overridePrimeNGTableMethods() {
    Paginator.prototype.onRppChange = function (e: object) {
      this.changePage(0);
    };
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}
